/**
 * 🗓 분기 실사 이력 — 데스크톱 재고관리 › [분기 실사] 탭 (v2.224.0 · 2026-10-01)
 *
 * 보기 전용 — 실사(판정·사진 올리기·지우기)는 홈 화면 앱(📦 재고 › 분기 재고 실사)에서 한다.
 *   숫자(시작·판정 장부·실사)를 누르면 품명&품목의 재고 팝업(PurchaseDetailModal · [입·출고 내역 상세보기] 포함)이 그대로 뜬다(v2.226.0).
 *   예외 하나: [✖ 실사 취소](v2.225.0 · 관리자급 · 보고서가 없을 때) — 기록·사진(NAS 파일까지) 모두 지운다. 확인 창은 앱과 같다.
 *   · 왼쪽: 해 고르기 + 그 해 분기 카드(세는 중 · 확인 끝 · 보고서 결재 중 · 완료)
 *   · 오른쪽: 고른 실사의 품목 표(시작 장부 · 판정 장부 · 실사 · 결과 · 메모 · 누가 언제 · 📷 사진) — 사진은 NAS 에서 그때 읽는다.
 * ⚠`?audit=<id>` 로 들어오면 그 실사를 바로 연다(보고서 모달의 링크).
 */

import { lazy, Suspense, useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import * as api from '../lib/stock-audit-api';
import type { AuditItem, AuditItemStatus, AuditSummary } from '../lib/stock-audit-api';
import { StockPhotoGallery } from './StockPhotoGallery';
import { dateTimeLabel } from '../lib/date-label';
import { cancelDoneText, confirmCancelAudit } from '../lib/stock-audit-cancel';
import { locText } from '../lib/stock-locations';
import { LoadingLines } from './ui/Skeleton';

// 품명&품목 탭의 재고 팝업과 같은 것(v2.226.0) — 처음 누를 때 불러온다
const PurchaseDetailModal = lazy(() =>
  import('./PurchaseDetailModal').then((m) => ({ default: m.PurchaseDetailModal })),
);

const STATUS_VIEW: Record<AuditItemStatus, { icon: string; label: string; cls: string }> = {
  pending: { icon: '⬜', label: '미확인', cls: 'text-slate-400' },
  later: { icon: '⏳', label: '나중에', cls: 'text-blue-700' },
  ok: { icon: '✅', label: '맞음', cls: 'text-emerald-700' },
  diff: { icon: '⚠️', label: '다름', cls: 'text-amber-700 font-semibold' },
  skip: { icon: '⏭', label: '무시', cls: 'text-slate-500' },
};

const STATE_CLS: Record<AuditSummary['state'], string> = {
  counting: 'bg-blue-100 text-blue-800',
  counted: 'bg-lime-100 text-lime-900',
  reviewing: 'bg-amber-100 text-amber-900',
  closed: 'bg-emerald-100 text-emerald-900',
};

function num(n: number | null | undefined): string {
  return n === null || n === undefined ? '—' : n.toLocaleString('ko-KR');
}

const TH = 'px-2 py-1.5 font-semibold text-slate-600 text-left align-bottom break-keep leading-tight';
/** 숫자 단추 — 품명&품목의 현재고 칸처럼 눌러서 재고 팝업 */
const NUM_BTN = 'tabular-nums text-blue-700 hover:underline cursor-pointer';
const TD = 'px-2 py-1.5 align-top';

export function StockAuditHistoryTab() {
  const [sp, setSp] = useSearchParams();
  const paramAudit = Number(sp.get('audit') ?? '');
  const [year, setYear] = useState<number>(new Date().getFullYear());
  const [sel, setSel] = useState<number | null>(Number.isFinite(paramAudit) && paramAudit > 0 ? paramAudit : null);
  const [filter, setFilter] = useState<'all' | 'diff' | 'skip' | 'left'>('all');
  const [gallery, setGallery] = useState<{ itemCode: string; itemName: string | null } | null>(null);
  // 숫자 누름 → 재고 팝업(품명&품목과 같은 것)
  const [history, setHistory] = useState<{ itemCode: string; itemName: string } | null>(null);
  const qc = useQueryClient();
  const [cancelBusy, setCancelBusy] = useState(false);
  const [cancelMsg, setCancelMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const list = useQuery({ queryKey: ['stock-audit', 'list', year], queryFn: () => api.listAudits(year), staleTime: 15_000 });
  const detail = useQuery({
    queryKey: ['stock-audit', 'detail', sel],
    queryFn: () => api.getAudit(sel!),
    enabled: sel !== null,
    staleTime: 15_000,
  });
  // 처음 들어와 고른 것이 없으면 그 해의 가장 최근 실사를 연다
  useEffect(() => {
    if (sel === null && list.data && list.data.audits.length > 0) {
      setSel(list.data.audits[list.data.audits.length - 1]!.id);
    }
  }, [list.data, sel]);
  // 보고서 모달에서 ?audit= 로 왔으면 그 해로 맞춘다
  useEffect(() => {
    const d = detail.data;
    if (d && d.audit.year !== year) setYear(d.audit.year);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail.data?.audit.year]);

  async function cancelSelected() {
    const cur = detail.data;
    if (!cur || cancelBusy) return;
    const photos = cur.items.reduce((n, it) => n + it.photoCount, 0);
    if (!confirmCancelAudit({ label: cur.audit.label, decided: cur.progress.decided, photos, adhoc: api.isAdhoc(cur.audit) })) return;
    setCancelBusy(true);
    setCancelMsg(null);
    try {
      const r = await api.cancelAudit(cur.audit.id);
      setCancelMsg({ ok: true, text: cancelDoneText(r, api.isAdhoc(cur.audit)) });
      // ⚠목록을 먼저 새로 받는다 — 옛 목록이 남은 채 sel 을 비우면 위의 「가장 최근 실사 열기」가 지운 실사를 다시 고른다
      await qc.invalidateQueries({ queryKey: ['stock-audit', 'list'] });
      qc.removeQueries({ queryKey: ['stock-audit', 'detail', cur.audit.id] });
      setSel(null);
      const next = new URLSearchParams(sp);
      next.delete('audit');
      setSp(next, { replace: true });
    } catch (e) {
      setCancelMsg({ ok: false, text: e instanceof Error ? e.message : String(e) });
    } finally {
      await qc.invalidateQueries({ queryKey: ['stock-audit'] });
      setCancelBusy(false);
    }
  }

  /** 품명&품목과 같은 재고 팝업 — 품목코드로 정확히 맞춘다 */
  function openHistory(it: AuditItem) {
    setHistory({ itemCode: it.itemCode, itemName: it.itemName ?? it.itemCode });
  }

  function pick(id: number) {
    setCancelMsg(null);
    setSel(id);
    const next = new URLSearchParams(sp);
    next.set('audit', String(id));
    setSp(next, { replace: true });
  }

  const d = detail.data;
  const rows: AuditItem[] = (d?.items ?? []).filter((it) =>
    filter === 'all' ? true : filter === 'left' ? it.status === 'pending' || it.status === 'later' : it.status === filter);

  return (
    <div className="flex flex-col lg:flex-row gap-4 min-w-0">
      {/* 왼쪽 — 해·분기 */}
      <aside className="lg:w-72 shrink-0 space-y-2">
        <div className="flex items-center justify-between bg-white rounded shadow-sm border border-slate-200 px-3 py-2">
          <button type="button" onClick={() => setYear(year - 1)} className="btn btn-secondary btn-icon">‹</button>
          <span className="font-bold">{year}년</span>
          <button type="button" onClick={() => setYear(year + 1)} className="btn btn-secondary btn-icon">›</button>
        </div>
        {list.isLoading && <div className="text-xs text-slate-500 px-1"><LoadingLines /></div>}
        {list.isError && <div className="text-xs text-red-600 px-1">{(list.error as Error).message}</div>}
        {list.data && [1, 2, 3, 4].map((q) => {
          const a = list.data!.audits.find((x) => !api.isAdhoc(x) && x.quarter === q);
          const on = !!a && a.id === sel;
          return (
            <button
              key={q}
              type="button"
              disabled={!a}
              onClick={() => a && pick(a.id)}
              className={`w-full text-left rounded border px-3 py-2.5 ${on ? 'border-blue-500 bg-blue-50' : 'border-slate-200 bg-white hover:bg-slate-50'} disabled:opacity-50 disabled:cursor-default`}
            >
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-slate-900">{q}분기</span>
                {a ? (
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${STATE_CLS[a.state]}`}>{a.state === 'closed' ? '✓ 완료' : a.stateLabel}</span>
                ) : (
                  <span className="text-xs text-slate-500">기록 없음</span>
                )}
              </div>
              {a && (
                <div className="mt-1 text-xs text-slate-500">
                  {a.progress.decided}/{a.progress.total} · 맞음 {a.progress.ok} · 다름 {a.progress.diff} · 무시 {a.progress.skip}
                  {a.report && <div>보고서 {a.report.docNo} · {a.report.status}</div>}
                </div>
              )}
            </button>
          );
        })}
        {/* 🔎 수시 실물확인 (v2.324.0 · #31) — 그 해 것만 · 보고서 없음 */}
        {list.data && (
          <div className="pt-1">
            <div className="px-1 pb-1 text-xs font-semibold text-slate-700">🔎 수시 실물확인 <span className="font-normal text-slate-400">{list.data.audits.filter((x) => api.isAdhoc(x)).length}건</span></div>
            {list.data.audits.filter((x) => api.isAdhoc(x)).length === 0 && (
              <div className="px-1 text-xs text-slate-400">{year}년 기록 없음</div>
            )}
            <div className="space-y-2">
              {list.data.audits.filter((x) => api.isAdhoc(x)).map((a) => (
                <button key={a.id} type="button" onClick={() => pick(a.id)}
                  className={`w-full text-left rounded border px-3 py-2.5 ${a.id === sel ? 'border-blue-500 bg-blue-50' : 'border-slate-200 bg-white hover:bg-slate-50'}`}>
                  <div className="flex items-start justify-between gap-2">
                    <span className="text-sm font-semibold text-slate-900 break-words min-w-0">{a.title ?? a.label}</span>
                    <span className={`shrink-0 text-xs font-semibold px-2 py-0.5 rounded-full ${STATE_CLS[a.state]}`}>{a.stateLabel}</span>
                  </div>
                  <div className="mt-1 text-xs text-slate-500">
                    {a.checkDate} · {a.progress.decided}/{a.progress.total} · 맞음 {a.progress.ok} · 다름 {a.progress.diff} · 무시 {a.progress.skip}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
        <p className="text-xs text-slate-500 px-1 pt-1">
          실사·수시 확인·사진 올리기·지우기는 홈 화면 앱(📦 재고 › 🗓 분기 재고 실사)에서. 분기 실사 보고서는 사내결재 문서함 [분기 재고 실사] 탭 · 수시 확인은 결재 없이 기록만.
        </p>
      </aside>

      {/* 오른쪽 — 품목 표 */}
      <div className="flex-1 min-w-0">
        {cancelMsg && (
          <div className={`mb-3 text-sm rounded border px-3 py-2 flex items-start gap-2 ${cancelMsg.ok ? 'bg-emerald-50 border-emerald-200 text-emerald-900' : 'bg-red-50 border-red-200 text-red-800'}`}>
            <span className="flex-1 min-w-0 break-words">{cancelMsg.ok ? '✅ ' : '⚠ '}{cancelMsg.text}</span>
            <button type="button" onClick={() => setCancelMsg(null)} className="shrink-0 px-1 text-slate-500 hover:text-slate-800" aria-label="알림 닫기">✕</button>
          </div>
        )}
        {sel === null ? (
          <div className="bg-white rounded shadow-sm border border-slate-200 p-10 text-center text-slate-400 text-sm">
            {list.data && list.data.audits.length === 0 ? `${year}년에는 분기 실사·수시 확인 기록이 없습니다.` : '왼쪽에서 분기 실사나 수시 확인을 고르세요.'}
          </div>
        ) : detail.isLoading ? (
          <div className="bg-white rounded shadow-sm border border-slate-200 p-10 text-center text-slate-400 text-sm"><LoadingLines /></div>
        ) : detail.isError || !d ? (
          <div className="bg-white rounded shadow-sm border border-slate-200 p-10 text-center text-red-600 text-sm">{detail.error ? (detail.error as Error).message : '불러오지 못했습니다'}</div>
        ) : (
          <div className="bg-white rounded shadow-sm border border-slate-200 min-w-0">
            <div className="px-4 py-3 border-b border-slate-200 space-y-1.5">
              <div className="flex items-start justify-between gap-3">
                <h3 className="ui-subsection-title">
                  {api.headingOf(d.audit)}
                  <span className={`ml-2 text-xs font-semibold px-2 py-0.5 rounded-full ${STATE_CLS[d.audit.state]}`}>{d.audit.stateLabel}</span>
                </h3>
                {d.canDelete && (
                  <button type="button" onClick={() => void cancelSelected()} disabled={cancelBusy}
                    className="btn btn-danger btn-sm shrink-0"
                    title={api.isAdhoc(d.audit) ? '이 수시 실물확인의 확인 기록·사진(NAS 파일까지)을 모두 지웁니다' : '이 분기 실사의 확인 기록·사진(NAS 파일까지)을 모두 지웁니다 — 같은 분기를 다시 시작할 수 있습니다'}>
                    {cancelBusy ? '지우는 중…' : '✖ 실사 취소 (모두 삭제)'}
                  </button>
                )}
              </div>
              <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-slate-600">
                <span><span className="text-slate-400 mr-1">시작</span>{dateTimeLabel(d.audit.startedAt)} {d.audit.startedByName}</span>
                <span><span className="text-slate-400 mr-1">대상</span>{d.audit.scopeCount}종</span>
                <span><span className="text-slate-400 mr-1">진행</span>{d.progress.decided}/{d.progress.total} · 맞음 {d.progress.ok} · <span className={d.progress.diff ? 'text-amber-700 font-semibold' : ''}>다름 {d.progress.diff}</span> · 무시 {d.progress.skip}{d.progress.later ? ` · 나중에 ${d.progress.later}` : ''}</span>
                {d.report && <span><span className="text-slate-400 mr-1">보고서</span>{d.report.docNo} · {d.report.status} · {d.report.authorName}</span>}
                {d.lock.locked && <span className="text-slate-500">🔒 {d.lock.reason}</span>}
              </div>
            </div>
            <div className="px-4 py-2 border-b border-slate-200 flex flex-wrap gap-1.5 text-xs">
              {([['all', '전체'], ['diff', '⚠ 다름'], ['skip', '⏭ 무시'], ['left', '남은 것']] as const).map(([k, l]) => (
                <button key={k} type="button" onClick={() => setFilter(k)}
                  className={`btn btn-sm ${filter === k ? 'btn-primary' : 'btn-secondary'}`}>{l}</button>
              ))}
            </div>
            <div className="p-4 overflow-x-auto">
              <table className="w-full text-xs border-collapse border border-slate-200 rounded">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className={`${TH} w-8`}>No</th>
                    <th className={TH}>품목</th>
                    <th className={TH}>분류</th>
                    <th className={`${TH} text-right`} title="실사를 시작한 순간의 현재고">시작<div className="font-normal text-slate-400">장부</div></th>
                    <th className={`${TH} text-right`} title="그 품목을 확인한 순간 다시 읽은 현재고">판정<div className="font-normal text-slate-400">장부</div></th>
                    <th className={`${TH} text-right`}>실사</th>
                    <th className={`${TH} text-right`}>차이</th>
                    <th className={TH}>결과</th>
                    <th className={TH}>메모</th>
                    <th className={TH}>확인<div className="font-normal text-slate-400">(누가·언제)</div></th>
                    <th className={`${TH} text-center`}>사진</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 && (
                    <tr><td colSpan={11} className="py-6 text-center text-slate-400">해당하는 품목이 없습니다.</td></tr>
                  )}
                  {rows.map((it) => {
                    const v = STATUS_VIEW[it.status];
                    const delta = it.status === 'diff' && it.countedQty !== null && it.systemQty !== null ? it.countedQty - it.systemQty : null;
                    return (
                      <tr key={it.itemCode} className={`border-b border-slate-100 last:border-b-0 ${it.status === 'diff' ? 'bg-amber-50/40' : ''}`}>
                        <td className={`${TD} text-slate-400`}>{it.ord}</td>
                        <td className={`${TD} min-w-[10rem]`}>
                          <div className="font-medium text-slate-900 break-words">{it.itemName ?? it.itemCode}</div>
                          <div className="text-[11px] text-slate-500">{it.itemCode}</div>
                        </td>
                        <td className={`${TD} whitespace-nowrap text-slate-600`}>{it.displayGroup ?? it.itemGroup ?? '—'}</td>
                        <td className={`${TD} text-right tabular-nums`}>
                          <button type="button" onClick={() => openHistory(it)} className={NUM_BTN} title="입·출고 내역 보기">{num(it.startQty)}</button>
                        </td>
                        <td className={`${TD} text-right tabular-nums ${it.systemQty !== null && it.systemQty !== it.startQty ? 'text-amber-700 font-semibold' : ''}`}>
                          {it.systemQty === null ? '—' : <button type="button" onClick={() => openHistory(it)} className={NUM_BTN} title="입·출고 내역 보기">{num(it.systemQty)}</button>}
                        </td>
                        <td className={`${TD} text-right tabular-nums`}>
                          {it.status === 'ok' || (it.status === 'diff' && it.countedQty !== null) ? (
                            <button type="button" onClick={() => openHistory(it)} className={NUM_BTN} title="입·출고 내역 보기">
                              {num(it.status === 'ok' ? it.systemQty : it.countedQty)}
                            </button>
                          ) : it.status === 'diff' ? <span className="text-slate-400">미기재</span> : '—'}
                          {/* 📍 장소별 센 수(나중에 일부 입력 포함) — 장소마다 한 줄 · 짧은 이름(「10층」·「B1」)으로 칸 폭을 안 늘린다
                              (한 줄로 「10층 공장 21 · B1 창고 1」을 붙였더니 1366 에서 표가 81px 넓어졌다 · 실측) */}
                          {it.locQty && (
                            <div className="text-[11px] font-normal text-slate-500 leading-tight" title={locText(it.locQty, d.locations ?? [])}>
                              {(d.locations ?? []).filter((l) => typeof it.locQty![l.key] === 'number').map((l) => (
                                <div key={l.key} className="whitespace-nowrap">{l.label.split(' ')[0]} {num(it.locQty![l.key]!)}</div>
                              ))}
                            </div>
                          )}
                        </td>
                        <td className={`${TD} text-right tabular-nums ${delta !== null && delta !== 0 ? 'text-rose-700 font-bold' : ''}`}>{delta === null ? '—' : `${delta > 0 ? '+' : ''}${num(delta)}`}</td>
                        <td className={`${TD} whitespace-nowrap ${v.cls}`}>{v.icon} {v.label}</td>
                        <td className={`${TD} min-w-[8rem] break-words whitespace-pre-line text-slate-700`}>{it.note ?? <span className="text-slate-300">—</span>}</td>
                        <td className={`${TD} whitespace-nowrap`}>
                          {it.decidedByName ? (<div><div className="text-slate-800">{it.decidedByName}</div><div className="text-[11px] text-slate-500 tabular-nums">{dateTimeLabel(it.decidedAt)}</div></div>) : <span className="text-slate-300">—</span>}
                        </td>
                        <td className={`${TD} text-center whitespace-nowrap`}>
                          {it.photoCount > 0 ? (
                            <button type="button" onClick={() => setGallery({ itemCode: it.itemCode, itemName: it.itemName })}
                              className="btn btn-secondary btn-sm" title="사진 보기 (NAS)">📷 {it.photoCount}</button>
                          ) : <span className="text-slate-300">—</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* 숫자 누름 → 품명&품목과 같은 재고 팝업(규격별 현재고 · 최근 입·출고 · [입·출고 내역 상세보기]) */}
      <Suspense fallback={null}>
        {history && (
          <PurchaseDetailModal
            itemCode={history.itemCode}
            itemName={history.itemName}
            displayName={history.itemName}
            variant="stock"
            onClose={() => setHistory(null)}
          />
        )}
      </Suspense>

      {gallery && sel !== null && d && (
        <StockPhotoGallery
          photoKey={{ scope: 'audit', auditId: sel }}
          itemCode={gallery.itemCode}
          itemName={gallery.itemName}
          title={api.headingOf(d.audit)}
          onClose={() => setGallery(null)}
        />
      )}
    </div>
  );
}
